import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Globe2, Lightbulb, Cog, DollarSign, UserIcon, HandshakeIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Define the feature interface
interface Feature {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  expandedContent: string;
}

// Define props interface for FeatureCard
interface FeatureCardProps {
  feature: Feature;
  isExpanded: boolean;
  onToggle: () => void;
  language: string;
}

// Define props interface for FeatureSections
interface FeatureSectionsProps {
  language?: string;
}

// Translations for 12 languages
const TRANSLATIONS: Record<string, {
  features: Feature[];
  buttonShowMore: string;
  buttonShowLess: string;
  buttonContactUs: string;
}> = {
  en: {
    features: [
      {
        icon: Globe2,
        title: "Global Excellence Through German Engineering",
        description: "Leveraging our German-engineered precision and global expertise, we deliver innovative solutions that adapt to market demands and seize international opportunities, fostering sustainable economic growth and technological advancement.",
        expandedContent: "Our robust network, rooted in German quality standards, ensures seamless collaboration across borders, driving cutting-edge innovation and reinforcing our leadership in the global marketplace."
      },
      {
        icon: Lightbulb,
        title: "Innovation Driven by German Precision",
        description: "At the core of our operations lies a commitment to innovation, guided by German engineering excellence, delivering pioneering solutions that set industry benchmarks and shape future trends.",
        expandedContent: "Our dedication to precision and forward-thinking ensures unparalleled efficiency and value, empowering our clients to achieve their goals with reliable, state-of-the-art technology."
      },
      {
        icon: Cog,
        title: "Sustainability as a German Principle",
        description: "Sustainability is a cornerstone of our philosophy, inspired by German standards of responsibility, harmonizing profitability with environmental and social well-being for a resilient global economy.",
        expandedContent: "We champion sustainable initiatives that align with international standards, ensuring long-term viability and ethical practices that benefit our partners and the planet."
      },
      {
        icon: DollarSign,
        title: "Efficiency Through German Expertise",
        description: "Our expertise, honed by German craftsmanship, drives operational efficiency and delivers measurable value, providing our clients with a competitive edge in a dynamic global market.",
        expandedContent: "By combining meticulous attention to detail with strategic insight, we optimize processes and outcomes, ensuring success and profitability for our stakeholders."
      },
      {
        icon: UserIcon,
        title: "Client-Centric Solutions, German Quality",
        description: "We prioritize our clients’ needs, offering tailored solutions crafted with German precision, fostering enduring partnerships built on trust and exceptional performance.",
        expandedContent: "Our client-first approach guarantees results that exceed expectations, delivering consistent quality and reliability that strengthen long-term collaboration."
      },
      {
        icon: HandshakeIcon,
        title: "Integrity Rooted in German Values",
        description: "Our unwavering commitment to ethics and integrity, a hallmark of German business culture, ensures transparency and responsibility in all operations, building trust and credibility worldwide.",
        expandedContent: "Adhering to the highest standards of professionalism, we cultivate lasting relationships and a reputation for excellence among our clients, partners, and communities."
      }
    ],
    buttonShowMore: "Show More",
    buttonShowLess: "Show Less",
    buttonContactUs: "Contact Us",
  },
  de: {
    features: [
      {
        icon: Globe2,
        title: "Globale Exzellenz durch deutsche Ingenieurskunst",
        description: "Mit unserer deutschen Ingenieurskunst und globalen Expertise liefern wir innovative Lösungen, die sich an Marktanforderungen anpassen und internationale Chancen nutzen, um nachhaltiges Wirtschaftswachstum und technologischen Fortschritt zu fördern.",
        expandedContent: "Unser starkes Netzwerk, geprägt von deutschen Qualitätsstandards, gewährleistet eine nahtlose Zusammenarbeit über Grenzen hinweg, treibt bahnbrechende Innovationen voran und festigt unsere Führungsposition im globalen Markt."
      },
      {
        icon: Lightbulb,
        title: "Innovation durch deutsche Präzision",
        description: "Im Mittelpunkt unserer Arbeit steht die Verpflichtung zur Innovation, geleitet von deutscher Ingenieursexzellenz, mit der wir wegweisende Lösungen schaffen, die Branchenmaßstäbe setzen und zukünftige Trends prägen.",
        expandedContent: "Unsere Hingabe an Präzision und Weitsicht garantiert höchste Effizienz und Wert, sodass unsere Kunden ihre Ziele mit verlässlicher, modernster Technologie erreichen können."
      },
      {
        icon: Cog,
        title: "Nachhaltigkeit als deutscher Grundsatz",
        description: "Nachhaltigkeit ist ein Eckpfeiler unserer Philosophie, inspiriert von deutschen Verantwortungsstandards, die Rentabilität mit ökologischem und sozialem Wohlstand für eine widerstandsfähige Weltwirtschaft in Einklang bringen.",
        expandedContent: "Wir fördern nachhaltige Initiativen, die internationalen Standards entsprechen, und sichern langfristige Lebensfähigkeit sowie ethische Praktiken zum Wohle unserer Partner und des Planeten."
      },
      {
        icon: DollarSign,
        title: "Effizienz durch deutsche Kompetenz",
        description: "Unsere durch deutsches Handwerk geschärfte Expertise steigert die betriebliche Effizienz und liefert messbaren Mehrwert, wodurch unsere Kunden einen Wettbewerbsvorteil in einem dynamischen globalen Markt erlangen.",
        expandedContent: "Durch die Kombination von akribischer Detailtreue und strategischer Weitsicht optimieren wir Prozesse und Ergebnisse, um den Erfolg und die Rentabilität unserer Stakeholder zu gewährleisten."
      },
      {
        icon: UserIcon,
        title: "Kundenzentrierte Lösungen, deutsche Qualität",
        description: "Wir stellen die Bedürfnisse unserer Kunden in den Vordergrund und bieten maßgeschneiderte Lösungen, die mit deutscher Präzision gefertigt sind, um dauerhafte Partnerschaften auf Vertrauen und herausragender Leistung aufzubauen.",
        expandedContent: "Unser kundenorientierter Ansatz garantiert Ergebnisse, die Erwartungen übertreffen, und liefert beständige Qualität und Zuverlässigkeit, die langfristige Zusammenarbeit stärken."
      },
      {
        icon: HandshakeIcon,
        title: "Integrität verwurzelt in deutschen Werten",
        description: "Unser unerschütterliches Engagement für Ethik und Integrität, ein Markenzeichen der deutschen Geschäftskultur, gewährleistet Transparenz und Verantwortung in allen Bereichen und schafft weltweit Vertrauen und Glaubwürdigkeit.",
        expandedContent: "Durch die Einhaltung höchster Professionalitätsstandards pflegen wir dauerhafte Beziehungen und einen Ruf für Exzellenz bei unseren Kunden, Partnern und Gemeinschaften."
      }
    ],
    buttonShowMore: "Mehr Anzeigen",
    buttonShowLess: "Weniger Anzeigen",
    buttonContactUs: "Kontaktieren Sie Uns",
  },
  es: {
    features: [
      {
        icon: Globe2,
        title: "Excelencia Global a Través de la Ingeniería Alemana",
        description: "Aprovechando la precisión de la ingeniería alemana y nuestra experiencia global, ofrecemos soluciones innovadoras que se adaptan a las demandas del mercado y aprovechan oportunidades internacionales, promoviendo un crecimiento económico sostenible y avances tecnológicos.",
        expandedContent: "Nuestra sólida red, basada en estándares de calidad alemanes, asegura una colaboración fluida a través de las fronteras, impulsando la innovación de vanguardia y reforzando nuestro liderazgo en el mercado global."
      },
      {
        icon: Lightbulb,
        title: "Innovación Impulsada por la Precisión Alemana",
        description: "En el núcleo de nuestras operaciones está el compromiso con la innovación, guiado por la excelencia en ingeniería alemana, ofreciendo soluciones pioneras que establecen estándares industriales y dan forma a las tendencias futuras.",
        expandedContent: "Nuestra dedicación a la precisión y al pensamiento avanzado asegura una eficiencia y valor incomparables, permitiendo a nuestros clientes alcanzar sus objetivos con tecnología confiable y de última generación."
      },
      {
        icon: Cog,
        title: "Sostenibilidad como Principio Alemán",
        description: "La sostenibilidad es una piedra angular de nuestra filosofía, inspirada en los estándares de responsabilidad alemanes, armonizando la rentabilidad con el bienestar ambiental y social para una economía global resiliente.",
        expandedContent: "Defendemos iniciativas sostenibles alineadas con estándares internacionales, asegurando viabilidad a largo plazo y prácticas éticas que benefician a nuestros socios y al planeta."
      },
      {
        icon: DollarSign,
        title: "Eficiencia a Través de la Experiencia Alemana",
        description: "Nuestra experiencia, perfeccionada por la artesanía alemana, impulsa la eficiencia operativa y entrega un valor medible, proporcionando a nuestros clientes una ventaja competitiva en un mercado global dinámico.",
        expandedContent: "Al combinar una atención meticulosa al detalle con una visión estratégica, optimizamos procesos y resultados, asegurando éxito y rentabilidad para nuestros interesados."
      },
      {
        icon: UserIcon,
        title: "Soluciones Centradas en el Cliente, Calidad Alemana",
        description: "Priorizamos las necesidades de nuestros clientes, ofreciendo soluciones personalizadas elaboradas con precisión alemana, fomentando asociaciones duraderas basadas en confianza y un rendimiento excepcional.",
        expandedContent: "Nuestro enfoque centrado en el cliente garantiza resultados que superan las expectativas, entregando calidad y confiabilidad consistentes que fortalecen la colaboración a largo plazo."
      },
      {
        icon: HandshakeIcon,
        title: "Integridad Arraigada en Valores Alemanes",
        description: "Nuestro compromiso inquebrantable con la ética y la integridad, un sello distintivo de la cultura empresarial alemana, asegura transparencia y responsabilidad en todas las operaciones, generando confianza y credibilidad a nivel mundial.",
        expandedContent: "Al adherirnos a los más altos estándares de profesionalismo, cultivamos relaciones duraderas y una reputación de excelencia entre nuestros clientes, socios y comunidades."
      }
    ],
    buttonShowMore: "Mostrar Más",
    buttonShowLess: "Mostrar Menos",
    buttonContactUs: "Contáctenos",
  },
  fr: {
    features: [
      {
        icon: Globe2,
        title: "Excellence Mondiale Grâce à l’Ingénierie Allemande",
        description: "En tirant parti de la précision de l’ingénierie allemande et de notre expertise mondiale, nous fournissons des solutions innovantes qui s’adaptent aux exigences du marché et saisissent les opportunités internationales, favorisant une croissance économique durable et des avancées technologiques.",
        expandedContent: "Notre réseau robuste, ancré dans les normes de qualité allemandes, garantit une collaboration sans faille à travers les frontières, stimulant une innovation de pointe et renforçant notre leadership sur le marché mondial."
      },
      {
        icon: Lightbulb,
        title: "Innovation Guidée par la Précision Allemande",
        description: "Au cœur de nos opérations se trouve un engagement envers l’innovation, guidé par l’excellence de l’ingénierie allemande, offrant des solutions pionnières qui établissent des références industrielles et façonnent les tendances futures.",
        expandedContent: "Notre dévouement à la précision et à la prospective garantit une efficacité et une valeur inégalées, permettant à nos clients d’atteindre leurs objectifs avec une technologie fiable et de pointe."
      },
      {
        icon: Cog,
        title: "Durabilité comme Principe Allemand",
        description: "La durabilité est une pierre angulaire de notre philosophie, inspirée par les normes de responsabilité allemandes, harmonisant la rentabilité avec le bien-être environnemental et social pour une économie mondiale résiliente.",
        expandedContent: "Nous soutenons des initiatives durables alignées sur les normes internationales, assurant une viabilité à long terme et des pratiques éthiques qui profitent à nos partenaires et à la planète."
      },
      {
        icon: DollarSign,
        title: "Efficacité Grâce à l’Expertise Allemande",
        description: "Notre expertise, affinée par l’artisanat allemand, améliore l’efficacité opérationnelle et offre une valeur mesurable, donnant à nos clients un avantage compétitif dans un marché mondial dynamique.",
        expandedContent: "En combinant une attention méticuleuse aux détails avec une vision stratégique, nous optimisons les processus et les résultats, garantissant succès et rentabilité pour nos parties prenantes."
      },
      {
        icon: UserIcon,
        title: "Solutions Axées sur le Client, Qualité Allemande",
        description: "Nous priorisons les besoins de nos clients, offrant des solutions sur mesure conçues avec la précision allemande, favorisant des partenariats durables basés sur la confiance et une performance exceptionnelle.",
        expandedContent: "Notre approche centrée sur le client garantit des résultats qui dépassent les attentes, offrant une qualité et une fiabilité constantes qui renforcent la collaboration à long terme."
      },
      {
        icon: HandshakeIcon,
        title: "Intégrité Enracinée dans les Valeurs Allemandes",
        description: "Notre engagement inébranlable envers l’éthique et l’intégrité, caractéristique de la culture d’entreprise allemande, assure transparence et responsabilité dans toutes les opérations, bâtissant confiance et crédibilité à l’échelle mondiale.",
        expandedContent: "En respectant les plus hauts standards de professionnalisme, nous cultivons des relations durables et une réputation d’excellence parmi nos clients, partenaires et communautés."
      }
    ],
    buttonShowMore: "En Voir Plus",
    buttonShowLess: "En Voir Moins",
    buttonContactUs: "Contactez-Nous",
  },
  it: {
    features: [
      {
        icon: Globe2,
        title: "Eccellenza Globale con l’Ingegneria Tedesca",
        description: "Sfruttando la precisione dell’ingegneria tedesca e la nostra esperienza globale, offriamo soluzioni innovative che si adattano alle esigenze del mercato e colgono opportunità internazionali, promuovendo una crescita economica sostenibile e progressi tecnologici.",
        expandedContent: "La nostra solida rete, radicata negli standard di qualità tedeschi, garantisce una collaborazione fluida oltre i confini, spingendo l’innovazione all’avanguardia e rafforzando la nostra leadership nel mercato globale."
      },
      {
        icon: Lightbulb,
        title: "Innovazione Guidata dalla Precisione Tedesca",
        description: "Al centro delle nostre operazioni c’è l’impegno per l’innovazione, guidato dall’eccellenza dell’ingegneria tedesca, offrendo soluzioni pionieristiche che stabiliscono parametri di riferimento per l’industria e modellano le tendenze future.",
        expandedContent: "La nostra dedizione alla precisione e al pensiero lungimirante garantisce efficienza e valore senza pari, consentendo ai nostri clienti di raggiungere i loro obiettivi con una tecnologia affidabile e all’avanguardia."
      },
      {
        icon: Cog,
        title: "Sostenibilità come Principio Tedesco",
        description: "La sostenibilità è un pilastro della nostra filosofia, ispirata agli standard di responsabilità tedeschi, armonizzando la redditività con il benessere ambientale e sociale per un’economia globale resiliente.",
        expandedContent: "Promuoviamo iniziative sostenibili in linea con gli standard internazionali, garantendo una sostenibilità a lungo termine e pratiche etiche che avvantaggiano i nostri partner e il pianeta."
      },
      {
        icon: DollarSign,
        title: "Efficienza con l’Esperienza Tedesca",
        description: "La nostra esperienza, affinata dall’artigianato tedesco, migliora l’efficienza operativa e offre un valore misurabile, fornendo ai nostri clienti un vantaggio competitivo in un mercato globale dinamico.",
        expandedContent: "Combinando un’attenzione meticolosa ai dettagli con una visione strategica, ottimizziamo processi e risultati, garantendo successo e redditività per i nostri stakeholder."
      },
      {
        icon: UserIcon,
        title: "Soluzioni Centrate sul Cliente, Qualità Tedesca",
        description: "Diamo priorità alle esigenze dei nostri clienti, offrendo soluzioni su misura realizzate con precisione tedesca, favorendo partnership durature basate sulla fiducia e su prestazioni eccezionali.",
        expandedContent: "Il nostro approccio incentrato sul cliente garantisce risultati che superano le aspettative, offrendo qualità e affidabilità costanti che rafforzano la collaborazione a lungo termine."
      },
      {
        icon: HandshakeIcon,
        title: "Integrità Radicata nei Valori Tedeschi",
        description: "Il nostro impegno incrollabile per l’etica e l’integrità, un segno distintivo della cultura aziendale tedesca, garantisce trasparenza e responsabilità in tutte le operazioni, costruendo fiducia e credibilità a livello globale.",
        expandedContent: "Rispettando i più alti standard di professionalità, coltiviamo relazioni durature e una reputazione di eccellenza tra i nostri clienti, partner e comunità."
      }
    ],
    buttonShowMore: "Mostra di Più",
    buttonShowLess: "Mostra di Meno",
    buttonContactUs: "Contattaci",
  },
  nl: {
    features: [
      {
        icon: Globe2,
        title: "Wereldwijde Excellentie door Duitse Ingenieurskunst",
        description: "Door gebruik te maken van onze Duitse technische precisie en wereldwijde expertise leveren we innovatieve oplossingen die zich aanpassen aan markteisen en internationale kansen benutten, wat duurzame economische groei en technologische vooruitgang bevordert.",
        expandedContent: "Ons robuuste netwerk, geworteld in Duitse kwaliteitsnormen, zorgt voor naadloze samenwerking over grenzen heen, stimuleert geavanceerde innovatie en versterkt onze leidende positie op de wereldmarkt."
      },
      {
        icon: Lightbulb,
        title: "Innovatie Gedreven door Duitse Precisie",
        description: "De kern van onze activiteiten wordt gevormd door een toewijding aan innovatie, geleid door Duitse technische excellentie, met baanbrekende oplossingen die industrienormen vaststellen en toekomstige trends vormgeven.",
        expandedContent: "Onze toewijding aan precisie en vooruitdenken garandeert ongeëvenaarde efficiëntie en waarde, waardoor onze klanten hun doelen kunnen bereiken met betrouwbare, state-of-the-art technologie."
      },
      {
        icon: Cog,
        title: "Duurzaamheid als Duits Principe",
        description: "Duurzaamheid is een hoeksteen van onze filosofie, geïnspireerd door Duitse normen van verantwoordelijkheid, waarbij winst in harmonie wordt gebracht met ecologisch en sociaal welzijn voor een veerkrachtige wereldeconomie.",
        expandedContent: "We ondersteunen duurzame initiatieven die aansluiten bij internationale normen, zorgen voor langetermijnlevensvatbaarheid en ethische praktijken die onze partners en de planeet ten goede komen."
      },
      {
        icon: DollarSign,
        title: "Efficiëntie door Duitse Expertise",
        description: "Onze expertise, aangescherpt door Duits vakmanschap, verhoogt de operationele efficiëntie en levert meetbare waarde, waardoor onze klanten een concurrentievoordeel behalen in een dynamische wereldmarkt.",
        expandedContent: "Door minutieuze aandacht voor detail te combineren met strategisch inzicht, optimaliseren we processen en resultaten, wat succes en winstgevendheid voor onze stakeholders garandeert."
      },
      {
        icon: UserIcon,
        title: "Klantgerichte Oplossingen, Duitse Kwaliteit",
        description: "We stellen de behoeften van onze klanten voorop en bieden op maat gemaakte oplossingen, vervaardigd met Duitse precisie, om duurzame partnerschappen te smeden gebaseerd op vertrouwen en uitzonderlijke prestaties.",
        expandedContent: "Onze klantgerichte benadering garandeert resultaten die de verwachtingen overtreffen, met consistente kwaliteit en betrouwbaarheid die langdurige samenwerking versterken."
      },
      {
        icon: HandshakeIcon,
        title: "Integriteit Geworteld in Duitse Waarden",
        description: "Onze onwrikbare toewijding aan ethiek en integriteit, een kenmerk van de Duitse bedrijfscultuur, zorgt voor transparantie en verantwoordelijkheid in al onze activiteiten, wat wereldwijd vertrouwen en geloofwaardigheid opbouwt.",
        expandedContent: "Door vast te houden aan de hoogste normen van professionaliteit, koesteren we duurzame relaties en een reputatie van excellentie onder onze klanten, partners en gemeenschappen."
      }
    ],
    buttonShowMore: "Toon Meer",
    buttonShowLess: "Toon Minder",
    buttonContactUs: "Neem Contact Op",
  },
  sa: {
    features: [
      {
        icon: Globe2,
        title: "التميز العالمي من خلال الهندسة الألمانية",
        description: "من خلال الاستفادة من الدقة الهندسية الألمانية وخبرتنا العالمية، نحن نقدم حلولًا مبتكرة تتكيف مع متطلبات السوق وتستغل الفرص الدولية، مما يعزز النمو الاقتصادي المستدام والتقدم التكنولوجي.",
        expandedContent: "شبكتنا القوية، المتجذرة في معايير الجودة الألمانية، تضمن تعاونًا سلسًا عبر الحدود، وتدفع الابتكار المتطور وتعزز ريادتنا في السوق العالمية."
      },
      {
        icon: Lightbulb,
        title: "الابتكار مدفوع بالدقة الألمانية",
        description: "في صميم عملياتنا يكمن التزامنا بالابتكار، بقيادة التميز الهندسي الألماني، مقدمين حلولًا رائدة تحدد معايير الصناعة وتشكل الاتجاهات المستقبلية.",
        expandedContent: "تفانينا في الدقة والتفكير المستقبلي يضمن كفاءة وقيمة لا مثيل لهما، مما يمكن عملاءنا من تحقيق أهدافهم باستخدام تكنولوجيا موثوقة وحديثة."
      },
      {
        icon: Cog,
        title: "الاستدامة كمبدأ ألماني",
        description: "الاستدامة هي حجر الزاوية في فلسفتنا، مستوحاة من معايير المسؤولية الألمانية، موازنة بين الربحية والرفاهية البيئية والاجتماعية لاقتصاد عالمي مرن.",
        expandedContent: "نحن ندعم المبادرات المستدامة التي تتماشى مع المعايير الدولية، مضمونين الجدوى طويلة الأمد والممارسات الأخلاقية التي تعود بالنفع على شركائنا والكوكب."
      },
      {
        icon: DollarSign,
        title: "الكفاءة من خلال الخبرة الألمانية",
        description: "خبرتنا، المصقولة بالحرفية الألمانية، تعزز الكفاءة التشغيلية وتقدم قيمة ملموسة، موفرة لعملائنا ميزة تنافسية في سوق عالمي ديناميكي.",
        expandedContent: "من خلال الجمع بين الاهتمام الدقيق بالتفاصيل والرؤية الاستراتيجية، نحن نُحسن العمليات والنتائج، مضمونين النجاح والربحية لأصحاب المصلحة لدينا."
      },
      {
        icon: UserIcon,
        title: "حلول تركز على العميل، جودة ألمانية",
        description: "نحن نعطي الأولوية لاحتياجات عملائنا، مقدمين حلولًا مخصصة مصنوعة بدقة ألمانية، معززين الشراكات الدائمة المبنية على الثقة والأداء الاستثنائي.",
        expandedContent: "نهجنا المتمحور حول العميل يضمن نتائج تتجاوز التوقعات، مقدمًا جودة وموثوقية متسقة تعزز التعاون طويل الأمد."
      },
      {
        icon: HandshakeIcon,
        title: "النزاهة المتجذرة في القيم الألمانية",
        description: "التزامنا الثابت بالأخلاق والنزاهة، وهو سمة مميزة للثقافة التجارية الألمانية، يضمن الشفافية والمسؤولية في جميع العمليات، مما يبني الثقة والمصداقية عالميًا.",
        expandedContent: "من خلال الالتزام بأعلى معايير الاحترافية، نحن نزرع علاقات دائمة وسمعة متميزة بين عملائنا وشركائنا ومجتمعاتنا."
      }
    ],
    buttonShowMore: "عرض المزيد",
    buttonShowLess: "عرض أقل",
    buttonContactUs: "اتصل بنا",
  },
  hk: {
    features: [
      {
        icon: Globe2,
        title: "德國工程打造全球卓越",
        description: "憑藉德國工程的精準和我們的全球專業知識，我們提供適應市場需求並抓住國際機遇的創新解決方案，促進可持續經濟增長和技術進步。",
        expandedContent: "我們植根於德國品質標準的強大網絡，確保跨國界的無縫合作，推動尖端創新並鞏固我們在全球市場的領導地位。"
      },
      {
        icon: Lightbulb,
        title: "德國精準驅動的創新",
        description: "我們運營的核心在於對創新的承諾，以德國工程卓越為引導，提供開創性的解決方案，設定行業基準並塑造未來趨勢。",
        expandedContent: "我們對精準和前瞻性思維的奉獻，確保無與倫比的效率和價值，使我們的客戶能夠以可靠、最先進的技術實現他們的目標。"
      },
      {
        icon: Cog,
        title: "德國原則下的可持續性",
        description: "可持續性是我們哲學的基石，受德國責任標準的啟發，將盈利能力與環境和社會福祉協調一致，打造一個有韌性的全球經濟。",
        expandedContent: "我們支持符合國際標準的可持續舉措，確保長期可行性和道德實踐，造福我們的合作夥伴和地球。"
      },
      {
        icon: DollarSign,
        title: "德國專長提升效率",
        description: "我們的專長，經由德國工藝打磨，提升運營效率並交付可衡量的價值，為客戶在動態的全球市場中提供競爭優勢。",
        expandedContent: "通過將對細節的精準關注與戰略洞察相結合，我們優化流程和成果，確保利益相關者的成功和盈利能力。"
      },
      {
        icon: UserIcon,
        title: "以客戶為中心的解決方案，德國品質",
        description: "我們優先考慮客戶的需求，提供以德國精準打造的定制解決方案，促進基於信任和卓越表現的持久合作關係。",
        expandedContent: "我們以客戶為先的方針保證超出預期的結果，提供一致的品質和可靠性，增強長期合作。"
      },
      {
        icon: HandshakeIcon,
        title: "植根於德國價值的誠信",
        description: "我們對道德和誠信的堅定承諾，是德國商業文化的標誌，確保所有運營中的透明度和責任感，建立全球信任和信譽。",
        expandedContent: "遵循最高的專業標準，我們培養與客戶、合作夥伴和社區的持久關係以及卓越聲譽。"
      }
    ],
    buttonShowMore: "顯示更多",
    buttonShowLess: "顯示較少",
    buttonContactUs: "聯繫我們",
  },
  sg: {
    features: [
      {
        icon: Globe2,
        title: "德国工程打造全球卓越",
        description: "凭借德国工程的精准和我们的全球专业知识，我们提供适应市场需求并抓住国际机遇的创新解决方案，促进可持续经济增长和技术进步。",
        expandedContent: "我们植根于德国品质标准的强大网络，确保跨国界的无缝合作，推动尖端创新并巩固我们在全球市场的领导地位。"
      },
      {
        icon: Lightbulb,
        title: "德国精准驱动的创新",
        description: "我们运营的核心在于对创新的承诺，以德国工程卓越为引导，提供开创性的解决方案，设定行业基准并塑造未来趋势。",
        expandedContent: "我们对精准和前瞻性思维的奉献，确保无与伦比的效率和价值，使我们的客户能够以可靠、最先进的技术实现他们的目标。"
      },
      {
        icon: Cog,
        title: "德国原则下的可持续性",
        description: "可持续性是我们哲学的基石，受德国责任标准的启发，将盈利能力与环境和社会福祉协调一致，打造一个有韧性的全球经济。",
        expandedContent: "我们支持符合国际标准的可持续举措，确保长期可行性和道德实践，造福我们的合作伙伴和地球。"
      },
      {
        icon: DollarSign,
        title: "德国专长提升效率",
        description: "我们的专长，经由德国工艺打磨，提升运营效率并交付可衡量的价值，为客户在动态的全球市场中提供竞争优势。",
        expandedContent: "通过将对细节的精准关注与战略洞察相结合，我们优化流程和成果，确保利益相关者的成功和盈利能力。"
      },
      {
        icon: UserIcon,
        title: "以客户为中心的解决方案，德国品质",
        description: "我们优先考虑客户的需求，提供以德国精准打造的定制解决方案，促进基于信任和卓越表现的持久合作关系。",
        expandedContent: "我们以客户为先的方针保证超出预期的结果，提供一致的品质和可靠性，增强长期合作。"
      },
      {
        icon: HandshakeIcon,
        title: "植根于德国价值的诚信",
        description: "我们对道德和诚信的坚定承诺，是德国商业文化的标志，确保所有运营中的透明度和责任感，建立全球信任和信誉。",
        expandedContent: "遵循最高的专业标准，我们培养与客户、合作伙伴和社区的持久关系以及卓越声誉。"
      }
    ],
    buttonShowMore: "显示更多",
    buttonShowLess: "显示较少",
    buttonContactUs: "联系我们",
  },
  kr: {
    features: [
      {
        icon: Globe2,
        title: "독일 엔지니어링으로 실현된 글로벌 우수성",
        description: "독일 엔지니어링의 정밀함과 글로벌 전문성을 바탕으로, 우리는 시장 요구에 적응하고 국제적 기회를 포착하는 혁신적인 솔루션을 제공하여 지속 가능한 경제 성장과 기술 발전을 촉진합니다.",
        expandedContent: "독일 품질 표준에 뿌리를 둔 우리의 견고한 네트워크는 국경을 초월한 원활한 협력을 보장하며, 최첨단 혁신을 주도하고 글로벌 시장에서의 리더십을 강화합니다."
      },
      {
        icon: Lightbulb,
        title: "독일 정밀함으로 이끄는 혁신",
        description: "우리 운영의 핵심에는 독일 엔지니어링 우수성에 기반한 혁신에 대한 헌신이 있으며, 산업 표준을 설정하고 미래 트렌드를 형성하는 선구적인 솔루션을 제공합니다.",
        expandedContent: "정밀함과 미래지향적 사고에 대한 우리의 헌신은 비교할 수 없는 효율성과 가치를 보장하며, 고객이 신뢰할 수 있는 최신 기술로 목표를 달성할 수 있도록 지원합니다."
      },
      {
        icon: Cog,
        title: "독일 원칙으로서의 지속 가능성",
        description: "지속 가능성은 독일의 책임 표준에서 영감을 받아 우리의 철학의 초석이며, 수익성을 환경 및 사회적 웰빙과 조화시켜 탄력 있는 글로벌 경제를 만듭니다.",
        expandedContent: "우리는 국제 표준에 부합하는 지속 가능한 이니셔티브를 지지하며, 장기적인 생존 가능성과 윤리적 관행을 보장하여 파트너와 지구에 이익을 줍니다."
      },
      {
        icon: DollarSign,
        title: "독일 전문성을 통한 효율성",
        description: "독일 장인 정신으로 연마된 우리의 전문성은 운영 효율성을 높이고 측정 가능한 가치를 제공하여, 역동적인 글로벌 시장에서 고객에게 경쟁 우위를 제공합니다.",
        expandedContent: "세부 사항에 대한 세심한 주의와 전략적 통찰력을 결합하여 프로세스와 결과를 최적화하며, 이해관계자의 성공과 수익성을 보장합니다."
      },
      {
        icon: UserIcon,
        title: "고객 중심 솔루션, 독일 품질",
        description: "우리는 고객의 요구를 최우선으로 하며, 독일 정밀함으로 제작된 맞춤형 솔루션을 제공하여 신뢰와 탁월한 성과를 기반으로 한 지속적인 파트너십을 육성합니다.",
        expandedContent: "고객 우선 접근 방식은 기대를 뛰어넘는 결과를 보장하며, 일관된 품질과 신뢰성을 제공하여 장기적인 협력을 강화합니다."
      },
      {
        icon: HandshakeIcon,
        title: "독일 가치에 뿌리내린 정직",
        description: "윤리와 정직에 대한 우리의 확고한 헌신은 독일 비즈니스 문화의 특징으로, 모든 운영에서 투명성과 책임감을 보장하며 전 세계적으로 신뢰와 신용을 구축합니다.",
        expandedContent: "최고의 전문성 표준을 준수함으로써, 우리는 고객, 파트너, 그리고 커뮤니티 간의 지속적인 관계와 탁월한 명성을 키웁니다."
      }
    ],
    buttonShowMore: "더 보기",
    buttonShowLess: "덜 보기",
    buttonContactUs: "문의하기",
  },
  jp: {
    features: [
      {
        icon: Globe2,
        title: "ドイツ工学によるグローバルな卓越性",
        description: "ドイツ工学の精密さとグローバルな専門知識を活用し、市場ニーズに適応し国際的な機会を捉える革新的なソリューションを提供することで、持続可能な経済成長と技術進歩を促進します。",
        expandedContent: "ドイツの品質基準に根ざした強固なネットワークは、国境を越えたシームレスな協力を保証し、先端的なイノベーションを推進し、グローバル市場でのリーダーシップを強化します。"
      },
      {
        icon: Lightbulb,
        title: "ドイツの精密さによるイノベーション",
        description: "我々の事業の中核には、ドイツ工学の卓越性に導かれたイノベーションへのコミットメントがあり、業界基準を確立し未来のトレンドを形成する先駆的なソリューションを提供します。",
        expandedContent: "精密さと先進的思考への献身は、比類のない効率性と価値を保証し、信頼性の高い最先端技術でクライアントが目標を達成できるよう支援します。"
      },
      {
        icon: Cog,
        title: "ドイツの原則としての持続可能性",
        description: "持続可能性は、ドイツの責任基準にインスパイアされた我々の哲学の基盤であり、収益性と環境および社会的幸福を調和させ、強靭なグローバル経済を築きます。",
        expandedContent: "我々は国際基準に適合する持続可能な取り組みを支持し、長期的な存続可能性と倫理的実践を確保し、パートナーと地球に利益をもたらします。"
      },
      {
        icon: DollarSign,
        title: "ドイツの専門知識による効率性",
        description: "ドイツの職人技で磨かれた我々の専門知識は、運用効率を向上させ、測定可能な価値を提供し、ダイナミックなグローバル市場でクライアントに競争優位性を与えます。",
        expandedContent: "細部への細心の注意と戦略的洞察を組み合わせることで、プロセスと成果を最適化し、ステークホルダーの成功と収益性を保証します。"
      },
      {
        icon: UserIcon,
        title: "顧客中心のソリューション、ドイツ品質",
        description: "我々はクライアントのニーズを優先し、ドイツの精密さで作られたカスタマイズされたソリューションを提供し、信頼と卓越したパフォーマンスに基づく永続的なパートナーシップを育みます。",
        expandedContent: "顧客第一のアプローチは期待を超える結果を保証し、一貫した品質と信頼性を提供し、長期的な協力を強化します。"
      },
      {
        icon: HandshakeIcon,
        title: "ドイツの価値に根ざした誠実さ",
        description: "倫理と誠実さへの我々の揺るぎないコミットメントは、ドイツのビジネス文化の特徴であり、すべての運営における透明性と責任を確保し、世界中で信頼と信用を築きます。",
        expandedContent: "最高のプロフェッショナリズム基準を遵守することで、クライアント、パートナー、コミュニティとの永続的な関係と卓越した評判を育みます。"
      }
    ],
    buttonShowMore: "もっと見る",
    buttonShowLess: "少なく見る",
    buttonContactUs: "お問い合わせ",
  },
  za: {
    features: [
      {
        icon: Globe2,
        title: "Wêreldwye Uitnemendheid deur Duitse Ingenieurswese",
        description: "Deur ons Duitse ingenieurspresisie en globale kundigheid te benut, lewer ons innoverende oplossings wat aanpassings maak vir markvereistes en internasionale geleenthede aangryp, wat volhoubare ekonomiese groei en tegnologiese vooruitgang bevorder.",
        expandedContent: "Ons robuuste netwerk, gewortel in Duitse kwaliteitsstandaarde, verseker naatlose samewerking oor grense heen, dryf voorpunt-innovasie en versterk ons leierskap in die globale mark."
      },
      {
        icon: Lightbulb,
        title: "Innovasie Gedryf deur Duitse Presisie",
        description: "Die kern van ons bedrywighede word gevorm deur ’n verbintenis tot innovasie, gelei deur Duitse ingenieursuitnemendheid, wat baanbrekende oplossings lewer wat industrie-standaarde stel en toekomstige tendense vormgee.",
        expandedContent: "Ons toewyding aan presisie en vooruitdenkende denke verseker ongeëwenaarde doeltreffendheid en waarde, wat ons kliënte bemagtig om hul doelwitte te bereik met betroubare, moderne tegnologie."
      },
      {
        icon: Cog,
        title: "Volhoubaarheid as ’n Duitse Beginsel",
        description: "Volhoubaarheid is ’n hoeksteen van ons filosofie, geïnspireer deur Duitse standaarde van verantwoordelikheid, wat winsgewendheid met omgewings- en sosiale welstand harmoniseer vir ’n veerkragtige wêreldekonomie.",
        expandedContent: "Ons ondersteun volhoubare inisiatiewe wat ooreenstem met internasionale standaarde, verseker langtermyn-lewensvatbaarheid en etiese praktyke wat ons vennote en die planeet bevoordeel."
      },
      {
        icon: DollarSign,
        title: "Doeltreffendheid deur Duitse Kundigheid",
        description: "Ons kundigheid, geslyp deur Duitse vakmanskap, verbeter operasionele doeltreffendheid en lewer meetbare waarde, wat ons kliënte ’n mededingende voordeel in ’n dinamiese globale mark bied.",
        expandedContent: "Deur noukeurige aandag aan detail met strategiese insig te kombineer, optimaliseer ons prosesse en uitkomste, wat sukses en winsgewendheid vir ons belanghebbendes verseker."
      },
      {
        icon: UserIcon,
        title: "Kliëntgesentreerde Oplossings, Duitse Kwaliteit",
        description: "Ons prioritiseer die behoeftes van ons kliënte en bied pasgemaakte oplossings, vervaardig met Duitse presisie, wat blywende vennootskappe bevorder wat op vertroue en uitsonderlike prestasie gebou is.",
        expandedContent: "Ons kliënt-eerste benadering waarborg resultate wat verwagtinge oortref, en lewer konsekwente kwaliteit en betroubaarheid wat langtermyn-samewerking versterk."
      },
      {
        icon: HandshakeIcon,
        title: "Integriteit Gewortel in Duitse Waardes",
        description: "Ons onwankelbare verbintenis tot etiek en integriteit, ’n kenmerk van die Duitse besigheidskultuur, verseker deursigtigheid en verantwoordelikheid in alle bedrywighede, wat wêreldwyd vertroue en geloofwaardigheid bou.",
        expandedContent: "Deur by die hoogste standaarde van professionaliteit te bly, kweek ons blywende verhoudings en ’n reputasie vir uitnemendheid onder ons kliënte, vennote en gemeenskappe."
      }
    ],
    buttonShowMore: "Wys Meer",
    buttonShowLess: "Wys Minder",
    buttonContactUs: "Kontak Ons",
  },
};

// FeatureCard Component
const FeatureCard: React.FC<FeatureCardProps> = ({ feature, isExpanded, onToggle, language }) => {
  const Icon = feature.icon;
  const translations = TRANSLATIONS[language] || TRANSLATIONS['en'];
  const navigate = useNavigate();

  const handlePassionClick = () => {
    navigate('/contactform');
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="feature-card relative bg-[#0B111F] rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 
                 border border-gray-700 backdrop-blur-lg overflow-hidden group"
    >
      <div className="absolute inset-0 bg-[#0B111F] opacity-80" />
      <div className="relative flex flex-col space-y-6">
        <div className="flex items-center space-x-4">
          <div className="icon-wrapper bg-[#FFFFFF] p-4 rounded-lg relative">
            <div className="absolute inset-0 bg-[#FFFFFF] blur-md opacity-50" />
            <Icon className="w-8 h-8 text-[#0B111F] relative z-10" />
          </div>
          <h3 className="text-white text-xl font-bold leading-tight flex-1 tracking-wide">
            {translations.features.find(f => f.icon === feature.icon)?.title || feature.title}
          </h3>
        </div>
        <div className="content-wrapper relative z-10">
          <p className="text-gray-300 mb-4 leading-relaxed">
            {translations.features.find(f => f.icon === feature.icon)?.description || feature.description}
          </p>
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: isExpanded ? 1 : 0, height: isExpanded ? 'auto' : 0 }}
            className="overflow-hidden"
          >
            <p className="text-gray-400 mb-6 leading-relaxed">
              {translations.features.find(f => f.icon === feature.icon)?.expandedContent || feature.expandedContent}
            </p>
          </motion.div>
          <div className="flex justify-between items-center mt-6">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={onToggle}
              className="px-2 py-2 rounded-[5px] bg-transparent text-white font-medium border border-[#FFFFFF] transition-all duration-200 hover:bg-[#0B111F] hover:text-white hover:shadow-[0px_0px_15px_5px_#0B111F]"
            >
              {isExpanded ? translations.buttonShowLess : translations.buttonShowMore}
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handlePassionClick}
              className="px-4 py-2 rounded-[5px] bg-[#FFFFFF] text-[0B111F] font-medium transition-all duration-200 hover:bg-[#0B111F] hover:text-white hover:shadow-[0px_0px_15px_5px_#FFFFFF]"
            >
              <span className="relative z-10">{translations.buttonContactUs}</span>
              <div className="" />
            </motion.button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// FeatureSections Component
const FeatureSections: React.FC<FeatureSectionsProps> = ({ language = 'en' }) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const translations = TRANSLATIONS[language] || TRANSLATIONS['en'];

  return (
    <div className="max-w-7xl mx-auto mb-10">
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
      >
        {translations.features.map((feature, index) => (
          <FeatureCard
            key={index}
            feature={feature}
            isExpanded={index === expandedIndex}
            onToggle={() => setExpandedIndex(index === expandedIndex ? null : index)}
            language={language}
          />
        ))}
      </motion.div>
    </div>
  );
};

export default FeatureSections;